import isEmpty from "lodash/isEmpty";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { SearchUsersUserDto } from "../../../model/admin/dto/SearchUsersUserDto";
import { UpdateUserThresholdsDto } from "../../../model/admin/dto/UpdateUserThresholdsDto";
import { ThresholdKinds } from "../../../model/threshold/types/ThresholdKinds.enum";
import { adminActions } from "../../../redux/admin/admin.slice";
import { useAppDispatch } from "../../../redux/store";
import { AtiraToast } from "../../../utils/AtiraToast";

type AdminUserThresholdsUpdateModalProps = {
  open: boolean;
  onClose: VoidFunction;
  user: Partial<SearchUsersUserDto> | null;
};

export const AdminUserThresholdsUpdateModal: React.FC<
  AdminUserThresholdsUpdateModalProps
> = ({ open = false, onClose, user }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const { control, getValues, handleSubmit, reset, watch } =
    useForm<UpdateUserThresholdsDto>();

  const userThresholds = user?.thresholds;

  const newThresholds = watch("newThresholds");

  const isButtonDisabled = Object.entries(newThresholds || {}).some(
    ([key, value]) => {
      if (!userThresholds) {
        return false;
      }

      return (
        Number(userThresholds[key as keyof typeof ThresholdKinds]) >
        Number(value)
      );
    },
  );

  const dispatch = useAppDispatch();

  const onUpdate = async () => {
    try {
      setLoading(true);

      const dto = getValues();
      dto.newThresholds = Object.entries(dto.newThresholds).reduce(
        (acc, [key, value]) => {
          if (
            user?.thresholds?.[key as keyof typeof ThresholdKinds] === value
          ) {
            return acc;
          } else {
            return { ...acc, [key]: value };
          }
        },
        {} as UpdateUserThresholdsDto["newThresholds"],
      );

      if (!isEmpty(dto.newThresholds)) {
        await dispatch(adminActions.updateUserThresholds(dto)).unwrap();
        AtiraToast.success(t("admin.user.update_thresholds.success"));
      }

      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user) {
      reset({ userId: user._id, newThresholds: user.thresholds });
    }
  }, [open, user, reset]);

  if (!user) {
    return null;
  }

  return (
    <Modal
      destroyOnClose
      open={open}
      onClose={onClose}
      title={t("admin.user.update_thresholds.modal.title", {
        name: user?.name || user?.email,
      })}
    >
      <Flex flexDirection="column" width={"100%"} gap="l">
        <Text color="darkerSub" align="center">
          {t("admin.user.update_thresholds.modal.note")}
        </Text>

        <Flex width={"100%"} flexDirection="column" gap="m">
          {Object.keys(ThresholdKinds)
            .filter((x) => x !== ThresholdKinds.MOBILE_APP)
            .map((kind, index) => (
              <Controller
                key={`${kind}-${index}`}
                control={control}
                name={`newThresholds.${kind as keyof typeof ThresholdKinds}`}
                render={({ field: { value, onChange } }) => (
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text fontWeight={"bold"}>
                      {t(`common.${kind.toLowerCase()}`)}:
                    </Text>

                    <Input
                      value={value}
                      type="number"
                      onChange={onChange}
                      min={1}
                      width="8rem"
                      valid={
                        Number(
                          userThresholds?.[kind as keyof typeof ThresholdKinds],
                        ) <= Number(value)
                      }
                    />
                  </Flex>
                )}
              />
            ))}

          <Button
            onClick={handleSubmit(onUpdate)}
            loading={loading}
            height="2.5rem"
            disabled={isButtonDisabled}
          >
            {t("common.update")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
